import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, lazy, Suspense } from 'react';
import { rtdb } from './firebase';
import { ref, get } from 'firebase/database';
import './pwa';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageLearningProvider, useLanguageLearning } from './contexts/LanguageLearningContext';
import { EntitlementsProvider } from './contexts/EntitlementsContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { useAuth } from './contexts/AuthContext';
import useStaleRequest from './hooks/useStaleRequest';
import { usePrerenderReadiness } from './lib/prerenderReadiness';
import { normalizeIntroUiLocale, resolveAprendosIntroEpisode } from './lib/featuredIntroEpisode';
import { installHoverPrefetch, prefetchDefaultBundles, prefetchRouteBundles } from './lib/routePrefetch';
import {
  buildCanonicalUrl,
  getBlogIndexAlternateUrls,
  getBlogIndexXDefaultUrl,
  getHomepageAlternateUrls,
  isBlogIndexPath,
  isBlogPostPath,
} from './lib/routeSeo';
import PageMeta from './components/PageMeta';
import PrivateLanguageRouteMeta from './components/PrivateLanguageRouteMeta';
import { isHomepageUiPath } from './lib/uiRouteLanguage';
import { DEFAULT_UI_LANGUAGE_CODE, SELECTABLE_UI_LANGUAGE_CODES, normalizeUiLanguageCode } from './constants/uiLanguages';
import { buildLearningPath } from './lib/learningRoutes';
import { isFreeConversationLanguage } from './lib/freeConversationCampaigns';
import './App.css';

// Synchronous imports for homepage and app shell
import Header from './components/Header';
import Footer from './components/Footer';
import HeroV2 from './components/HeroV2';
const LevelSelector = lazy(() => import('./components/LevelSelector'));
const FeaturedPodcasts = lazy(() => import('./components/FeaturedPodcasts'));
const LearningPath = lazy(() => import('./components/LearningPath'));
const StoreLanding = lazy(() => import('./components/StoreLanding'));
const CorporateDashboard = lazy(() => import('./components/corporate/CorporateDashboard'));
const CorporateEmployeeDashboard = lazy(() => import('./components/corporate/CorporateEmployeeDashboard'));
const CorporateSubnav = lazy(() => import('./components/corporate/CorporateSubnav'));
const CorporateRoute = lazy(() => import('./components/CorporateRoute'));
const CorporateMySessions = lazy(() => import('./components/corporate/CorporateMySessions'));
const CorporateTrainerSlots = lazy(() => import('./components/corporate/CorporateTrainerSlots'));
const DiscoveryBookingPage = lazy(() => import('./components/booking/DiscoveryBookingPage'));
const TrainerRoute = lazy(() => import('./components/TrainerRoute'));
const PremierPadelDemoPage = lazy(() => import('./components/corporate/PremierPadelDemoPage'));
const PremierPadelDemosPage = lazy(() => import('./components/corporate/PremierPadelDemosPage'));
const AprendosCorporateLanding = lazy(() => import('./components/corporate/AprendosCorporateLanding'));
const CorporateTrainingLanding = lazy(() => import('./components/corporate/CorporateTrainingLanding'));
const CorporateReadinessScorecard = lazy(() => import('./components/corporate/CorporateReadinessScorecard'));
import ProtectedRoute from './components/ProtectedRoute';
const AuthModal = lazy(() => import('./components/AuthModal'));
import LanguageRouteWrapper from './components/LanguageRouteWrapper';
import UnprefixedLearningRoute from './components/UnprefixedLearningRoute';
import UiLanguageRouteWrapper from './components/UiLanguageRouteWrapper';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import MobileInit from './components/MobileInit';
import AnalyticsPageTracker from './components/AnalyticsPageTracker';
import CookieConsentBanner from './components/CookieConsentBanner';

// Page loading fallback: gentle skeleton pulse instead of a generic spinner,
// so the layout feels alive while route bundles load.
function PageLoader() {
  return (
    <div
      className="min-h-[60vh] bg-[#FDFBF7] px-4 sm:px-6 lg:px-8 py-16"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="max-w-[1200px] mx-auto animate-pulse">
        <div className="h-3 w-24 bg-stone-200 rounded mb-5" />
        <div className="h-10 sm:h-12 w-2/3 max-w-xl bg-stone-200 rounded-lg mb-4" />
        <div className="h-4 w-1/2 max-w-md bg-stone-100 rounded mb-12" />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <div className="h-48 bg-stone-100 rounded-3xl" />
          <div className="h-48 bg-stone-100 rounded-3xl" />
          <div className="h-48 bg-stone-100 rounded-3xl hidden lg:block" />
        </div>
      </div>
    </div>
  );
}

// Lazy loaded route components
const FreeConversationsLanding = lazy(() => import('./components/free-conversations/FreeConversationsLanding'));
const FreeConversationEpisode = lazy(() => import('./components/free-conversations/FreeConversationEpisode'));
const FreeClassSignup = lazy(() => import('./components/free-conversations/FreeClassSignup'));
const LiveClassesConfirmation = lazy(() => import('./components/free-conversations/LiveClassesConfirmation'));
const Store = lazy(() => import('./components/Store'));
const GradedReaderDetail = lazy(() => import('./components/GradedReaders/Detail/GradedReaderDetail'));
const SeriesDetail = lazy(() => import('./components/GradedReaders/Detail/SeriesDetail'));
const EpubReader = lazy(() => import('./components/GradedReaders/Reader/EpubReader'));
const UnifiedReader = lazy(() => import('./components/GradedReaders/Reader/UnifiedReader'));
const MeetCreator = lazy(() => import('./components/MeetCreator'));
// const MeetTheHosts = lazy(() => import('./components/MeetTheHosts')); // Hidden: page is not public
const Methodology = lazy(() => import('./components/Methodology'));
const PublicPlacementPage = lazy(() => import('./components/PublicPlacementPage'));
const PodcastLessons = lazy(() => import('./components/PodcastLessons'));
const EpisodePlayerPage = lazy(() => import('./components/EpisodePlayerPage'));
const Community = lazy(() => import('./components/Community'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const FarewellPage = lazy(() => import('./components/FarewellPage'));
const LearningGuide = lazy(() => import('./components/LearningGuide'));
const LearningTips = lazy(() => import('./components/LearningTips'));
const FAQ = lazy(() => import('./components/FAQ'));
const ContactForm = lazy(() => import('./components/ContactForm'));
const FeedbackForm = lazy(() => import('./components/FeedbackForm'));
const TechnicalSupport = lazy(() => import('./components/TechnicalSupport'));
const Blog = lazy(() => import('./components/Blog'));
const BlogPost = lazy(() => import('./components/BlogPost'));
const BlogAdmin = lazy(() => import('./components/admin/Blog/BlogAdmin'));
const BlogEditor = lazy(() => import('./components/admin/Blog/BlogEditor'));
const SeoContentReviewDashboard = lazy(() => import('./components/admin/SeoContentReviewDashboard'));
const SeoDraftPreview = lazy(() => import('./components/admin/SeoDraftPreview'));
const LanguageSelection = lazy(() => import('./components/LanguageSelection'));
const LearningPaths = lazy(() => import('./components/LearningPaths'));
const SpacedRepetitionReview = lazy(() => import('./components/SpacedRepetitionReview'));
const VocabularyDeck = lazy(() => import('./components/VocabularyDeck'));
const MobileTestPage = lazy(() => import('./components/MobileTestPage'));
const Checkout = lazy(() => import('./components/Checkout'));
const SpeakingMinutesPacks = lazy(() => import('./components/SpeakingMinutesPacks'));
const SettingsPage = lazy(() => import('./components/Settings/SettingsPage'));
const AchievementsPage = lazy(() => import('./components/achievements/AchievementsPage'));
const LibraryDesignLab = lazy(() => import('./components/library/LibraryDesignLab'));
const LibraryHome = lazy(() => import('./components/library/LibraryHome'));
const CollectionDetail = lazy(() => import('./components/library/CollectionDetail'));
const CertificateVerifyPage = lazy(() => import('./components/CertificateVerifyPage'));
const VerifyEmailPage = lazy(() => import('./components/VerifyEmailPage'));
const LegalPage = lazy(() => import('./components/LegalPage'));
const NotFound = lazy(() => import('./components/NotFound'));
const LearnNorwegian = lazy(() => import('./components/LearnNorwegian'));
const LearnSpanish = lazy(() => import('./components/LearnSpanish'));
const LearnEnglish = lazy(() => import('./components/LearnEnglish'));
const SpanishConversationPracticeLanding = lazy(() => import('./components/SpanishConversationPracticeLanding'));

const PADEL_DEMO_EPISODE_IDS = [
  'DEMO-PADEL-PRESS-CHINGALAN',
  'DEMO-PADEL-INTERVIEW-GEMMA',
];

// Language Lab Components (lazy loaded)
const Coach = lazy(() => import('./components/Coach'));
const ArticleDetail = lazy(() => import('./components/language-lab/ArticleDetail'));
const PodcastDetail = lazy(() => import('./components/language-lab/PodcastDetail'));
const LanguageLabAdmin = lazy(() => import('./components/language-lab/LanguageLabAdmin'));

// Admin Components (lazy loaded)
const HostsSetup = lazy(() => import('./components/admin/HostsSetup'));
const AudiogramTool = lazy(() => import('./components/admin/AudiogramTool/AudiogramTool'));
const EntitlementsAdmin = lazy(() => import('./components/admin/EntitlementsAdmin'));
const CertificatesAdmin = lazy(() => import('./components/admin/CertificatesAdmin'));
const TranscriptConverter = lazy(() => import('./components/admin/TranscriptConverter'));
const ExtraPracticeExercisePage = lazy(() => import('./components/ExtraPracticeModule/ExtraPracticeExercisePage'));
const ExtraPracticeAdmin = lazy(() => import('./components/admin/ExtraPractice/ExtraPracticeAdmin'));
const RegularExerciseAdmin = lazy(() => import('./components/admin/RegularExercises/RegularExerciseAdmin'));

// Onboarding Components (lazy loaded)
const OnboardingLayout = lazy(() => import('./components/onboarding/OnboardingLayout'));
const LanguageSelectionScreen = lazy(() => import('./components/onboarding/LanguageSelectionScreen'));
const WelcomeScreen = lazy(() => import('./components/onboarding/WelcomeScreen'));
const LevelSelectionScreen = lazy(() => import('./components/onboarding/LevelSelectionScreen'));
const CompletionScreen = lazy(() => import('./components/onboarding/CompletionScreen'));
const PlacementTestWrapper = lazy(() => import('./components/onboarding/PlacementTestWrapper'));

function FreeConversationsLegacyRoute() {
  const { lang } = useParams();
  if (isFreeConversationLanguage(lang)) {
    return <FreeConversationsLanding />;
  }
  return <Navigate to="/es/live-classes" replace />;
}

function LegacyFreeConversationsSignupRedirect() {
  const { lang } = useParams();
  const location = useLocation();
  const targetLanguage = isFreeConversationLanguage(lang) ? lang : 'es';
  const searchParams = new URLSearchParams(location.search);
  searchParams.set('intent', 'paid');
  const query = searchParams.toString();

  return <Navigate to={`/${targetLanguage}/live-classes/signup${query ? `?${query}` : ''}`} replace />;
}

function LegacyFreeConversationsLandingRedirect() {
  const { lang } = useParams();
  const targetLanguage = isFreeConversationLanguage(lang) ? lang : 'es';
  return <Navigate to={`/${targetLanguage}/live-classes`} replace />;
}

function HomePage() {
  const { t, i18n } = useTranslation('pages');
  const currentLanguage = normalizeIntroUiLocale(i18n.resolvedLanguage || i18n.language);

  const { data: featuredEpisode, loading: featuredEpisodeLoading, error: featuredEpisodeError } = useStaleRequest({
    cacheKey: `home:featuredEpisode:${currentLanguage}`,
    loader: async () => {
      const episodesRef = ref(rtdb, 'episodes');
      const snapshot = await get(episodesRef);
      const episodesData = snapshot.exists() ? snapshot.val() : {};
      const episodes = Object.entries(episodesData || {});

      if (episodes[0]) {
        return await resolveAprendosIntroEpisode(
          episodes,
          currentLanguage,
          t('hero.player.whatIsAprendosTitle'),
        );
      }

      return await resolveAprendosIntroEpisode(
        [],
        currentLanguage,
        t('hero.player.whatIsAprendosTitle'),
      );
    },
    ttlMs: 2 * 60 * 1000,
    deps: [currentLanguage, t],
    initialValue: null,
  });
  usePrerenderReadiness('featured-episode', {
    loading: featuredEpisodeLoading,
    error: featuredEpisodeError,
    isEmpty: !featuredEpisode,
  });

  return (
    <>
      <PageMeta
        title={t('seo.home.title')}
        description={t('seo.home.description')}
      />
      <HeroV2 episode={featuredEpisode} />
      <Suspense fallback={<PageLoader />}>
        <div className="text-white">
          <LevelSelector />
          <FeaturedPodcasts />
          <LearningPath />
        </div>
        <StoreLanding />
      </Suspense>
    </>
  );
}

function FixedUiLanguageRoute({ languageCode, children }) {
  const { i18n } = useTranslation();
  const currentLanguage = normalizeUiLanguageCode(i18n.resolvedLanguage || i18n.language);
  const targetLanguage = normalizeUiLanguageCode(languageCode);

  useEffect(() => {
    if (currentLanguage !== targetLanguage) {
      void i18n.changeLanguage(targetLanguage);
    }
  }, [currentLanguage, i18n, targetLanguage]);

  if (currentLanguage !== targetLanguage) {
    return <PageLoader />;
  }

  return children;
}

function LocalizedHomePageRoute() {
  const { lang } = useParams();
  const location = useLocation();
  const normalizedLanguage = normalizeUiLanguageCode(lang);

  if (!SELECTABLE_UI_LANGUAGE_CODES.includes(normalizedLanguage)) {
    return (
      <Navigate
        to={{
          pathname: '/',
          search: location.search,
          hash: location.hash,
        }}
        replace
      />
    );
  }

  if (normalizedLanguage === DEFAULT_UI_LANGUAGE_CODE) {
    return (
      <Navigate
        to={{
          pathname: '/',
          search: location.search,
          hash: location.hash,
        }}
        replace
      />
    );
  }

  return (
    <FixedUiLanguageRoute languageCode={normalizedLanguage}>
      <HomePage />
    </FixedUiLanguageRoute>
  );
}

function ScrollToTopOnRouteChange() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const cleanup = () => {
      if (typeof document === 'undefined') return;

      document.body.style.pointerEvents = '';
      document.body.style.overflow = '';
      document.documentElement.style.pointerEvents = '';
      document.documentElement.style.overflow = '';
    };

    const rafId = window.requestAnimationFrame(() => {
      cleanup();
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [location.pathname, location.search, location.hash]);

  return null;
}

function RoutePrefetcher() {
  const location = useLocation();

  // Automatic prefetch is a production speed-up; in dev it floods the unbundled module server. Hover prefetch stays on.
  useEffect(() => {
    if (import.meta.env.DEV) return undefined;
    const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 80));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const handle = schedule(() => prefetchRouteBundles(location.pathname));

    return () => cancel(handle);
  }, [location.pathname]);

  useEffect(() => {
    if (import.meta.env.DEV) return undefined;
    const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 120));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const handle = schedule(() => prefetchDefaultBundles());

    return () => cancel(handle);
  }, []);

  useEffect(() => {
    const cleanup = installHoverPrefetch();
    return cleanup;
  }, []);

  return null;
}

function EmailVerificationGate() {
  const { currentUser, emailVerificationRequired, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;

    if (!currentUser && location.pathname === '/verify-email') {
      navigate('/', { replace: true });
      return;
    }

    if (currentUser && emailVerificationRequired && location.pathname !== '/verify-email') {
      navigate('/verify-email', { replace: true });
    }
  }, [currentUser, emailVerificationRequired, loading, location.pathname, navigate]);

  return null;
}

function RouteSeo() {
  const location = useLocation();

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    document.querySelectorAll('link[data-aprendos-hreflang="true"]').forEach((node) => {
      node.remove();
    });

    if (isBlogPostPath(location.pathname)) {
      return;
    }

    const canonicalUrl = buildCanonicalUrl(location.pathname);
    if (!canonicalUrl) {
      return;
    }

    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    let ogUrlMeta = document.querySelector('meta[property="og:url"]');
    if (!ogUrlMeta) {
      ogUrlMeta = document.createElement('meta');
      ogUrlMeta.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrlMeta);
    }
    ogUrlMeta.setAttribute('content', canonicalUrl);

    let alternateUrls = [];
    let xDefaultHref = '';

    if (isHomepageUiPath(location.pathname)) {
      alternateUrls = getHomepageAlternateUrls();
      xDefaultHref = `${window.location.origin}/`;
    } else if (isBlogIndexPath(location.pathname)) {
      alternateUrls = getBlogIndexAlternateUrls();
      xDefaultHref = getBlogIndexXDefaultUrl();
    }

    alternateUrls.forEach(({ hrefLang, href }) => {
      const alternateLink = document.createElement('link');
      alternateLink.setAttribute('rel', 'alternate');
      alternateLink.setAttribute('hreflang', hrefLang);
      alternateLink.setAttribute('href', href);
      alternateLink.setAttribute('data-aprendos-hreflang', 'true');
      document.head.appendChild(alternateLink);
    });

    if (xDefaultHref) {
      const xDefaultLink = document.createElement('link');
      xDefaultLink.setAttribute('rel', 'alternate');
      xDefaultLink.setAttribute('hreflang', 'x-default');
      xDefaultLink.setAttribute('href', xDefaultHref);
      xDefaultLink.setAttribute('data-aprendos-hreflang', 'true');
      document.head.appendChild(xDefaultLink);
    }
  }, [location.pathname]);

  return null;
}

function AuthBookingRoute() {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  const [showAuth, setShowAuth] = useState(false);
  const { t } = useTranslation();

  if (loading) return <PageLoader />;
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl border border-stone-200 bg-white p-6 text-center">
          <h2 className="text-xl font-semibold text-stone-900 font-[family-name:var(--font-display)] mb-2">
            {t('corporate.signInRequired')}
          </h2>
          <p className="text-sm text-stone-600 mb-4">{t('corporate.signInRequiredDescriptionGeneric')}</p>
          <button onClick={() => setShowAuth(true)} className="inline-flex items-center gap-2 rounded-full bg-stone-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-stone-800 transition-colors">
            {t('corporate.signIn')}
          </button>
          <Suspense fallback={null}>
            <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} mode="login" redirectTo={location.pathname} />
          </Suspense>
        </div>
      </div>
    );
  }
  return <CorporateMySessions />;
}

function CorporateBookingRoute() {
  const { orgId } = useParams();
  return (
    <CorporateRoute allowLearner orgIdFromPath={orgId}>
      {({ orgId: resolvedOrgId, orgName, memberRoles }) => (
        <>
          <Suspense fallback={null}><CorporateSubnav orgId={resolvedOrgId} roles={memberRoles} orgName={orgName} /></Suspense>
          <CorporateMySessions orgId={resolvedOrgId} orgName={orgName} />
        </>
      )}
    </CorporateRoute>
  );
}

function TrainerPageRoute() {
  return (
    <TrainerRoute>
      <CorporateTrainerSlots />
    </TrainerRoute>
  );
}

function LegacyCoachRedirect() {
  const location = useLocation();
  const langMatch = location.pathname.match(/^\/([a-z]{2})(?:\/|$)/i);
  const lang = langMatch?.[1] || 'en';

  return (
    <Navigate
      to={{
        pathname: `/${lang}/coach`,
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
}

function RenamedRouteRedirect({ to }) {
  const location = useLocation();

  return (
    <Navigate
      to={{
        pathname: to,
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
}

function LegacyCoachExerciseRedirect() {
  const location = useLocation();
  const { exerciseId } = useParams();
  const langMatch = location.pathname.match(/^\/([a-z]{2})(?:\/|$)/i);
  const lang = langMatch?.[1] || 'en';

  return (
    <Navigate
      to={{
        pathname: `/${lang}/coach/exercise/${exerciseId}`,
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
}

function DashboardRouteRedirect() {
  const location = useLocation();
  const { activeLanguage, loading } = useLanguageLearning();

  if (loading) {
    return <PageLoader />;
  }

  return (
    <Navigate
      to={{
        pathname: buildLearningPath('/dashboard', activeLanguage || 'en'),
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
}

function PadelDemoLessonsRoute() {
  const { lang } = useParams();
  const demoBasePath = `/${lang || 'en'}/__protected/padel-press-chingalan-demo`;

  return (
    <LanguageRouteWrapper>
      <PodcastLessons
        visibleEpisodeIds={PADEL_DEMO_EPISODE_IDS}
        episodeLinkBase={`${demoBasePath}/lessons`}
        hideUtilityBar
      />
    </LanguageRouteWrapper>
  );
}

function PadelDemoEpisodeRoute() {
  const { lang, episodeId } = useParams();
  const resolvedLang = lang || 'en';
  const demoBasePath = `/${resolvedLang}/__protected/padel-press-chingalan-demo`;

  const normalizedEpisodeId = String(episodeId || '').trim().toUpperCase();

  if (!PADEL_DEMO_EPISODE_IDS.includes(normalizedEpisodeId)) {
    return <Navigate to={demoBasePath} replace />;
  }

  return (
    <LanguageRouteWrapper>
      <EpisodePlayerPage
        lessonsPathOverride={demoBasePath}
        lessonRedirectPathOverride={`${demoBasePath}/lessons/${episodeId}`}
        skipProgressionCheck
        alwaysShowPreListeningFirst
      />
    </LanguageRouteWrapper>
  );
}

function LegacyPadelDemoLessonsRedirect() {
  return <Navigate to="/en/__protected/padel-press-chingalan-demo" replace />;
}

function LegacyPadelDemoEpisodeRedirect() {
  const { episodeId } = useParams();
  return <Navigate to={`/en/__protected/padel-press-chingalan-demo/lessons/${episodeId}`} replace />;
}

function LegacyLanguagePlacementTestRedirect() {
  const { lang } = useParams();
  const location = useLocation();
  const targetLanguage = String(lang || 'es').trim().toLowerCase() || 'es';

  return (
    <Navigate
      to={{
        pathname: `/${targetLanguage}/placement-test`,
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
}

const FOCUSED_CAMPAIGN_ROUTE_PATTERN = /^\/(?:(?:en|es|no)\/)?(?:listening|conversation)-practice\/?$/i;

function isFocusedCampaignRoute(pathname) {
  return FOCUSED_CAMPAIGN_ROUTE_PATTERN.test(pathname);
}

// Component to conditionally render Header based on route
function ConditionalHeader() {
  const location = useLocation();
  const useIntegratedPlayer = true; // Integrated player is permanently enabled

  // Hide header on episode player page when integrated player is enabled
  const isEpisodePlayerRoute = location.pathname.includes('/lessons/');
  const isOnboardingRoute = location.pathname === '/onboarding'
    || /^\/[a-z]{2}\/onboarding(?:\/|$)/i.test(location.pathname);
  const isFreeConversationsRoute = location.pathname.startsWith('/free-conversations');
  const isLiveClassesRoute = /^\/[a-z]{2}\/live-classes(?:\/|$)/i.test(location.pathname);
  const isFreeClassSignupRoute = location.pathname === '/clases'
    || location.pathname === '/classes'
    || /^\/free-conversations\/(?:es|en|no)\/signup(?:\/)?$/i.test(location.pathname);
  const isDiscoveryRoute = location.pathname === '/discovery';
  const isStandaloneCorporateRoute = location.pathname === '/corporate'
    || location.pathname === '/corporate-training'
    || location.pathname.startsWith('/corporate-training/');
  const shouldHideHeader = (isEpisodePlayerRoute && useIntegratedPlayer) || isOnboardingRoute || isFreeConversationsRoute || isFreeClassSignupRoute || isLiveClassesRoute || isDiscoveryRoute || isStandaloneCorporateRoute || isFocusedCampaignRoute(location.pathname);

  if (shouldHideHeader) {
    return null;
  }

  return <Header />;
}

// Component to conditionally render Footer based on route
function ConditionalFooter() {
  const location = useLocation();
  const useIntegratedPlayer = true;

  // Hide footer on episode player page when integrated player is enabled
  const isEpisodePlayerRoute = location.pathname.includes('/lessons/');
  const isOnboardingRoute = location.pathname === '/onboarding'
    || /^\/[a-z]{2}\/onboarding(?:\/|$)/i.test(location.pathname);
  const isFreeConversationsRoute = location.pathname.startsWith('/free-conversations');
  const isLiveClassesRoute = /^\/[a-z]{2}\/live-classes(?:\/|$)/i.test(location.pathname);
  const isFreeClassSignupRoute = location.pathname === '/clases'
    || location.pathname === '/classes'
    || /^\/free-conversations\/(?:es|en|no)\/signup(?:\/)?$/i.test(location.pathname);
  const isDiscoveryRoute = location.pathname === '/discovery';
  const isStandaloneCorporateRoute = location.pathname === '/corporate'
    || location.pathname === '/corporate-training'
    || location.pathname.startsWith('/corporate-training/');
  const shouldHideFooter = (isEpisodePlayerRoute && useIntegratedPlayer) || isOnboardingRoute || isFreeConversationsRoute || isFreeClassSignupRoute || isLiveClassesRoute || isDiscoveryRoute || isStandaloneCorporateRoute || isFocusedCampaignRoute(location.pathname);

  if (shouldHideFooter) {
    return null;
  }

  return <Footer />;
}

function ConditionalPWAInstallPrompt() {
  const location = useLocation();

  if (isFocusedCampaignRoute(location.pathname)) {
    return null;
  }

  return <PWAInstallPrompt />;
}

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Set document direction based on language
    const isRTL = i18n.language === 'ar';
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <AuthProvider>
      <LanguageLearningProvider>
        <OnboardingProvider>
        <EntitlementsProvider>
        <MobileInit>
          <Router>
          <EmailVerificationGate />
          <AnalyticsPageTracker />
          <RouteSeo />
          <ScrollToTopOnRouteChange />
          <RoutePrefetcher />
          <PrivateLanguageRouteMeta>
          <Suspense fallback={<PageLoader />}>
          <Routes>
          {/* Onboarding route (language selection - no header/footer) */}
          <Route path="/onboarding" element={<OnboardingLayout />}>
            <Route index element={<LanguageSelectionScreen />} />
          </Route>
          {/* Onboarding routes (language-specific - no header/footer) */}
          <Route path="/:lang/onboarding" element={<OnboardingLayout />}>
            <Route path="welcome" element={<WelcomeScreen />} />
            <Route path="level-select" element={<LevelSelectionScreen />} />
            <Route path="complete" element={<CompletionScreen />} />
          </Route>
          {/* Placement test route - separate from OnboardingLayout to allow full width */}
          <Route path="/:lang/onboarding/placement-test" element={<PlacementTestWrapper />} />
           <Route path="/farewell" element={<FarewellPage />} />
           <Route path="/verify" element={<CertificateVerifyPage />} />
           <Route path="/verify-email" element={<VerifyEmailPage />} />
           <Route path="*" element={
            <div className="flex flex-col min-h-screen">
              <ConditionalHeader />
              <main id="main-content" className="flex-grow">
                <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Root routes (no language prefix - redirect to default language) */}
                  <Route path="/" element={<FixedUiLanguageRoute languageCode="en"><HomePage /></FixedUiLanguageRoute>} />
                  <Route path="/:lang" element={<LocalizedHomePageRoute />} />
                  <Route path="/onboarding" element={<OnboardingLayout />}>
                    <Route index element={<LanguageSelectionScreen />} />
                  </Route>
                  <Route path="/:lang/onboarding" element={<OnboardingLayout />}>
                    <Route path="welcome" element={<WelcomeScreen />} />
                    <Route path="level-select" element={<LevelSelectionScreen />} />
                    <Route path="complete" element={<CompletionScreen />} />
                  </Route>
                  <Route path="/:lang/onboarding/placement-test" element={<PlacementTestWrapper />} />
                  {/* Free Conversations (public, no auth) */}
                  <Route path="/clases" element={<Navigate to="/es/live-classes/signup?intent=paid" replace />} />
                  <Route path="/classes" element={<Navigate to="/en/live-classes/signup?intent=paid" replace />} />
                  <Route path="/free-conversations" element={<Navigate to="/es/live-classes" replace />} />
                  <Route path="/:lang/live-classes" element={<FreeConversationsLegacyRoute />} />
                  <Route path="/:lang/live-classes/signup" element={<FreeClassSignup />} />
                  <Route path="/:lang/live-classes/confirmation" element={<LiveClassesConfirmation />} />
                  <Route path="/free-conversations/:lang" element={<LegacyFreeConversationsLandingRedirect />} />
                  <Route path="/free-conversations/:lang/signup" element={<LegacyFreeConversationsSignupRedirect />} />
                  <Route path="/free-conversations/:lang/:episodeId" element={<FreeConversationEpisode />} />

                  <Route path="/store" element={<Store />} />
                  <Route path="/store/graded-readers" element={<Store />} />
                  {/* Graded Readers - Series/Story routes (new structure) */}
                  <Route path="/store/graded-readers/:seriesId/:storyId/read" element={<UnifiedReader />} />
                  <Route path="/store/graded-readers/:seriesId/:storyId" element={<SeriesDetail />} />
                  {/* Graded Readers - Series detail page */}
                  <Route path="/store/graded-readers/:seriesId" element={<SeriesDetail />} />
                  {/* Legacy single reader routes (backward compatibility) */}
                  <Route path="/store/readers/:readerId" element={<GradedReaderDetail />} />
                  <Route path="/store/readers/:readerId/read" element={<EpubReader />} />
                  <Route path="/choose-language" element={<LanguageSelection />} />
                  
                  {/* Language-specific learning routes */}
                  <Route path="/:lang/learn-norwegian" element={<LanguageRouteWrapper><LearnNorwegian /></LanguageRouteWrapper>} />
                  <Route path="/:lang/learn-spanish" element={<LanguageRouteWrapper><LearnSpanish /></LanguageRouteWrapper>} />
                  <Route path="/:lang/learn-english" element={<LanguageRouteWrapper><LearnEnglish /></LanguageRouteWrapper>} />
                  {/* English landing pages live unprefixed; the /en/ URLs were renamed. */}
                  <Route path="/learn-english" element={<LanguageRouteWrapper languageCode="en"><LearnEnglish /></LanguageRouteWrapper>} />
                  <Route path="/en/learn-english" element={<RenamedRouteRedirect to="/learn-english" />} />
                  <Route path="/listening-practice" element={<FixedUiLanguageRoute languageCode="en"><SpanishConversationPracticeLanding language="en" /></FixedUiLanguageRoute>} />
                  <Route path="/en/listening-practice" element={<RenamedRouteRedirect to="/listening-practice" />} />
                  <Route path="/en/conversation-practice" element={<RenamedRouteRedirect to="/listening-practice" />} />
                  <Route path="/es/listening-practice" element={<FixedUiLanguageRoute languageCode="es"><SpanishConversationPracticeLanding language="es" /></FixedUiLanguageRoute>} />
                  <Route path="/es/conversation-practice" element={<Navigate to="/es/listening-practice" replace />} />
                  <Route path="/no/listening-practice" element={<FixedUiLanguageRoute languageCode="no"><SpanishConversationPracticeLanding language="no" /></FixedUiLanguageRoute>} />
                  <Route path="/no/conversation-practice" element={<Navigate to="/no/listening-practice" replace />} />
                  <Route path="/:lang/meet-creator" element={<LanguageRouteWrapper><MeetCreator /></LanguageRouteWrapper>} />
                  {/* Hidden: meet-the-hosts page is not public. Falls through to NotFound. */}
                  <Route path="/:lang/methodology" element={<LanguageRouteWrapper><Methodology /></LanguageRouteWrapper>} />
                  <Route path="/:lang/placement-test" element={<LanguageRouteWrapper><PublicPlacementPage /></LanguageRouteWrapper>} />
                  <Route path="/:lang/spanish-placement-test" element={<LegacyLanguagePlacementTestRedirect />} />
                  <Route path="/:lang/norwegian-placement-test" element={<LegacyLanguagePlacementTestRedirect />} />
                  <Route path="/:lang/__protected/padel-press-chingalan-demo" element={<PadelDemoLessonsRoute />} />
                  <Route path="/:lang/__protected/padel-press-chingalan-demo/lessons/:episodeId" element={<PadelDemoEpisodeRoute />} />
                  <Route path="/:lang/podcast-lessons" element={<LanguageRouteWrapper><PodcastLessons /></LanguageRouteWrapper>} />
                  <Route path="/:lang/lessons/:episodeId" element={<LanguageRouteWrapper><EpisodePlayerPage /></LanguageRouteWrapper>} />
                  <Route path="/:lang/learning-paths" element={<LanguageRouteWrapper><LearningPaths /></LanguageRouteWrapper>} />
                  <Route path="/:lang/checkout" element={<LanguageRouteWrapper><Checkout /></LanguageRouteWrapper>} />
                  <Route path="/:lang/speaking-minutes" element={<LanguageRouteWrapper><SpeakingMinutesPacks /></LanguageRouteWrapper>} />
                  <Route path="/:lang/community" element={<LanguageRouteWrapper><Community /></LanguageRouteWrapper>} />
                  <Route path="/:lang/dashboard" element={<LanguageRouteWrapper><Dashboard /></LanguageRouteWrapper>} />
                  <Route path="/:lang/vocabulary-review/session" element={<LanguageRouteWrapper><SpacedRepetitionReview /></LanguageRouteWrapper>} />
                  <Route path="/:lang/settings" element={<LanguageRouteWrapper><SettingsPage /></LanguageRouteWrapper>} />
                  <Route path="/:lang/achievements" element={<LanguageRouteWrapper><AchievementsPage /></LanguageRouteWrapper>} />
                  <Route path="/:lang/learning-guide" element={<LanguageRouteWrapper><LearningGuide /></LanguageRouteWrapper>} />
                  <Route path="/:lang/learning-tips" element={<LanguageRouteWrapper><LearningTips /></LanguageRouteWrapper>} />
                  <Route path="/:lang/library" element={<LanguageRouteWrapper><LibraryHome /></LanguageRouteWrapper>} />
                  <Route path="/:lang/library/collection/:seriesId/:storyId/read" element={<LanguageRouteWrapper><UnifiedReader /></LanguageRouteWrapper>} />
                  <Route path="/:lang/library/collection/:collectionId" element={<LanguageRouteWrapper><CollectionDetail /></LanguageRouteWrapper>} />
                  <Route path="/:lang/library/design-lab" element={<LanguageRouteWrapper><LibraryDesignLab /></LanguageRouteWrapper>} />
                  <Route path="/:lang/store" element={<UiLanguageRouteWrapper><Store /></UiLanguageRouteWrapper>} />
                  <Route path="/:lang/faq" element={<UiLanguageRouteWrapper><FAQ /></UiLanguageRouteWrapper>} />
                  <Route path="/:lang/contact" element={<UiLanguageRouteWrapper><ContactForm /></UiLanguageRouteWrapper>} />
                  <Route path="/:lang/feedback" element={<UiLanguageRouteWrapper><FeedbackForm /></UiLanguageRouteWrapper>} />
                  <Route path="/:lang/technical-support" element={<UiLanguageRouteWrapper><TechnicalSupport /></UiLanguageRouteWrapper>} />
                  <Route path="/:lang/blog" element={<UiLanguageRouteWrapper><Blog /></UiLanguageRouteWrapper>} />
                  <Route path="/:lang/blog/:slug" element={<UiLanguageRouteWrapper><BlogPost /></UiLanguageRouteWrapper>} />
                  <Route path="/:lang/privacy" element={<UiLanguageRouteWrapper><LegalPage type="privacy" /></UiLanguageRouteWrapper>} />
                  <Route path="/:lang/terms" element={<UiLanguageRouteWrapper><LegalPage type="terms" /></UiLanguageRouteWrapper>} />
                  <Route path="/:lang/cookies" element={<UiLanguageRouteWrapper><LegalPage type="cookies" /></UiLanguageRouteWrapper>} />
                  <Route path="/:lang/account-deletion" element={<UiLanguageRouteWrapper><LegalPage type="accountDeletion" /></UiLanguageRouteWrapper>} />

                  {/* General routes (no language prefix) */}
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/contact" element={<ContactForm />} />
                  <Route path="/feedback" element={<FeedbackForm />} />
                  <Route path="/technical-support" element={<TechnicalSupport />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/privacy" element={<LegalPage type="privacy" />} />
                  <Route path="/terms" element={<LegalPage type="terms" />} />
                  <Route path="/cookies" element={<LegalPage type="cookies" />} />
                  <Route path="/account-deletion" element={<LegalPage type="accountDeletion" />} />
                  <Route path="/vocabulary-review/session" element={<SpacedRepetitionReview />} />
                  <Route path="/vocabulary-deck" element={<VocabularyDeck />} />
                  <Route path="/mobile-test" element={<MobileTestPage />} />
                  
                  {/* Coach Routes (language-specific) */}
                  <Route path="/:lang/coach" element={<LanguageRouteWrapper><Coach /></LanguageRouteWrapper>} />
                  {/* Legacy routes redirect to Coach */}
                  <Route path="/:lang/language-hub" element={<LanguageRouteWrapper><Coach /></LanguageRouteWrapper>} />
                  <Route path="/:lang/language-lab" element={<LanguageRouteWrapper><Coach /></LanguageRouteWrapper>} />
                  <Route path="/:lang/language-lab/article/:id" element={<LanguageRouteWrapper><ArticleDetail /></LanguageRouteWrapper>} />
                  <Route path="/:lang/language-lab/podcast/:id" element={<LanguageRouteWrapper><PodcastDetail /></LanguageRouteWrapper>} />
                  <Route path="/:lang/language-lab/extra-practice" element={<LegacyCoachRedirect />} />
                  <Route path="/:lang/extra-practice" element={<LegacyCoachRedirect />} />
                  <Route path="/:lang/coach/exercise/:exerciseId" element={<LanguageRouteWrapper><ExtraPracticeExercisePage /></LanguageRouteWrapper>} />
                  <Route path="/:lang/extra-practice/exercise/:exerciseId" element={<LegacyCoachExerciseRedirect />} />
                  <Route path="/:lang/language-hub/admin" element={
                    <LanguageRouteWrapper>
                      <ProtectedRoute>
                        <LanguageLabAdmin />
                      </ProtectedRoute>
                    </LanguageRouteWrapper>
                  } />
                  <Route path="/admin/hosts-setup" element={
                    <ProtectedRoute>
                      <HostsSetup />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/audiogram" element={
                    <ProtectedRoute>
                      <AudiogramTool />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/entitlements" element={
                    <ProtectedRoute>
                      <EntitlementsAdmin />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/certificates" element={
                    <ProtectedRoute>
                      <CertificatesAdmin />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/blog" element={
                    <ProtectedRoute>
                      <BlogAdmin />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/blog/:id" element={
                    <ProtectedRoute>
                      <BlogEditor />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/seo-content" element={
                    <ProtectedRoute>
                      <SeoContentReviewDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/seo-content/preview/:projectId/:language" element={
                    <ProtectedRoute>
                      <SeoDraftPreview />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/transcript-converter" element={
                    <ProtectedRoute>
                      <TranscriptConverter />
                    </ProtectedRoute>
                  } />
                  <Route path="/:lang/admin/extra-practice" element={
                    <ProtectedRoute>
                      <ExtraPracticeAdmin />
                    </ProtectedRoute>
                  } />
                  <Route path="/:lang/admin/exercises" element={
                    <ProtectedRoute>
                      <RegularExerciseAdmin />
                    </ProtectedRoute>
                  } />

                  {/* Corporate landing page */}
                  <Route path="/corporate" element={<AprendosCorporateLanding />} />
                  <Route path="/corporate-training" element={<CorporateTrainingLanding />} />
                  <Route path="/corporate-training/readiness" element={<CorporateReadinessScorecard />} />
                  {/* Corporate Premier Padel public demo */}
                  <Route path="/corporate/premier-padel-demo" element={<PremierPadelDemoPage />} />
                  <Route path="/corporate/premier-padel/demos" element={<PremierPadelDemosPage />} />
                  {/* Corporate Dashboard (supervisor) */}
                  <Route path="/corporate/dashboard" element={
                    <Suspense fallback={<PageLoader />}>
                      <CorporateRoute>
                        {({ orgId, orgName, memberRoles }) => (
                          <>
                            <Suspense fallback={null}><CorporateSubnav orgId={orgId} roles={memberRoles} orgName={orgName} /></Suspense>
                            <CorporateDashboard orgId={orgId} orgName={orgName} />
                          </>
                        )}
                      </CorporateRoute>
                    </Suspense>
                  } />

                  {/* Corporate Employee Dashboard */}
                  <Route path="/corporate/employee" element={
                    <Suspense fallback={<PageLoader />}>
                      <CorporateRoute allowLearner>
                        {({ orgId, orgName, memberRoles }) => (
                          <>
                            <Suspense fallback={null}><CorporateSubnav orgId={orgId} roles={memberRoles} orgName={orgName} /></Suspense>
                            <CorporateEmployeeDashboard orgId={orgId} />
                          </>
                        )}
                      </CorporateRoute>
                    </Suspense>
                  } />

                  {/* 1:1 Session Booking */}
                  <Route path="/book" element={
                    <Suspense fallback={<PageLoader />}>
                      <AuthBookingRoute />
                    </Suspense>
                  } />
                  <Route path="/book/:orgId" element={
                    <Suspense fallback={<PageLoader />}>
                      <CorporateBookingRoute />
                    </Suspense>
                  } />
                  <Route path="/discovery" element={
                    <Suspense fallback={<PageLoader />}>
                      <DiscoveryBookingPage />
                    </Suspense>
                  } />

                  {/* Trainer slot management */}
                  <Route path="/trainer" element={
                    <Suspense fallback={<PageLoader />}>
                      <TrainerPageRoute />
                    </Suspense>
                  } />

                  {/* Legacy routes (backward compatibility - redirect to default language) */}
                  <Route path="/meet-creator" element={<MeetCreator />} />
                  {/* Hidden: meet-the-hosts page is not public. Falls through to NotFound. */}
                  <Route path="/methodology" element={<Methodology />} />
                  <Route path="/learn-norwegian" element={<Navigate to="/no/learn-norwegian" replace />} />
                  <Route path="/learn-spanish" element={<Navigate to="/es/learn-spanish" replace />} />
                  <Route path="/placement-test" element={<Navigate to="/choose-language?intent=placement-test" replace />} />
                  <Route path="/spanish-placement-test" element={<Navigate to="/es/placement-test" replace />} />
                  <Route path="/norwegian-placement-test" element={<Navigate to="/no/placement-test" replace />} />
                  <Route path="/__protected/padel-press-chingalan-demo" element={<LegacyPadelDemoLessonsRedirect />} />
                  <Route path="/__protected/padel-press-chingalan-demo/lessons/:episodeId" element={<LegacyPadelDemoEpisodeRedirect />} />
                  <Route path="/podcast-lessons" element={<UnprefixedLearningRoute><PodcastLessons /></UnprefixedLearningRoute>} />
                  <Route path="/lessons/:episodeId" element={<EpisodePlayerPage />} />
                  <Route path="/learning-paths" element={<UnprefixedLearningRoute><LearningPaths /></UnprefixedLearningRoute>} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/speaking-minutes" element={<SpeakingMinutesPacks />} />
                  <Route path="/community" element={<Community />} />
                  <Route path="/dashboard" element={<DashboardRouteRedirect />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/achievements" element={<AchievementsPage />} />
                  <Route path="/learning-guide" element={<LearningGuide />} />
                  <Route path="/learning-tips" element={<LearningTips />} />
                  <Route path="/library" element={<UnprefixedLearningRoute><LibraryHome /></UnprefixedLearningRoute>} />
                  <Route path="/library/collection/:seriesId/:storyId/read" element={<UnifiedReader />} />
                  <Route path="/library/collection/:collectionId" element={<CollectionDetail />} />
                  <Route path="/library/design-lab" element={<LibraryDesignLab />} />
                  <Route path="/coach" element={<UnprefixedLearningRoute><Coach /></UnprefixedLearningRoute>} />
                  <Route path="/coach/exercise/:exerciseId" element={<ExtraPracticeExercisePage />} />
                  {/* Legacy routes */}
                  <Route path="/language-hub" element={<Coach />} />
                  <Route path="/language-lab" element={<Coach />} />
                  <Route path="/language-lab/extra-practice" element={<LegacyCoachRedirect />} />
                  <Route path="/extra-practice" element={<LegacyCoachRedirect />} />
                  <Route path="/extra-practice/exercise/:exerciseId" element={<LegacyCoachExerciseRedirect />} />
                  <Route path="/language-lab/article/:id" element={<ArticleDetail />} />
                  <Route path="/language-lab/podcast/:id" element={<PodcastDetail />} />
                  <Route path="/language-hub/admin" element={
                    <ProtectedRoute>
                      <LanguageLabAdmin />
                    </ProtectedRoute>
                  } />

                  {/* Catch-all: branded 404 page */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </Suspense>
              </main>
              <ConditionalFooter />
            </div>
          } />
          </Routes>
          </Suspense>
          </PrivateLanguageRouteMeta>
          <ConditionalPWAInstallPrompt />
          </Router>
          <CookieConsentBanner />
        </MobileInit>
        </EntitlementsProvider>
        </OnboardingProvider>
      </LanguageLearningProvider>
    </AuthProvider>
  );
}

export default App;
